import { useState, useCallback, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import toast from "components/partials/toast/toast";
import { ConfirmationDialog } from "ui/confirmation-dialog";
import type { TouchpointAttributes } from "hooks/api/use-touchpoints";
import { touchpointKeys, touchpointVersionKeys } from "hooks/api/query-keys";
import { getTouchpointVersion } from "generated/api/touchpoint-version";
import { getActionTouchpointTypeLabel, getErrorMessage } from "utilities";

const touchpointVersionApi = getTouchpointVersion();

interface UseUpdateMergeTagsModalProps {
  onSuccess?: () => void;
}

export const useUpdateMergeTagsModal = ({ onSuccess }: UseUpdateMergeTagsModalProps = {}) => {
  const queryClient = useQueryClient();
  const [itemsToUpdate, setItemsToUpdate] = useState<TouchpointAttributes[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const singleItem = itemsToUpdate.length === 1 ? itemsToUpdate[0] : undefined;
  const singleItemTypeLabel = singleItem ? getActionTouchpointTypeLabel(singleItem.type) : undefined;

  const openUpdateMergeTagsModal = useCallback(
    (items: TouchpointAttributes | TouchpointAttributes[]) => {
      setItemsToUpdate(Array.isArray(items) ? items : [items]);
    },
    [],
  );

  const closeUpdateMergeTagsModal = useCallback(() => {
    setItemsToUpdate([]);
  }, []);

  const handleUpdateMergeTags = useCallback(async () => {
    if (itemsToUpdate.length === 0) return;

    const showUpdateFailedToast = () =>
      toast.error({
        data: {
          title: `Failed to update merge tags for ${singleItemTypeLabel?.toLowerCase() ?? "creatives"}`,
        },
      });

    try {
      setIsLoading(true);
      const settled = await Promise.allSettled(
        itemsToUpdate.map((item) =>
          touchpointVersionApi.updateMergeTags({
            clientId: item.clientId,
            campaignId: item.campaignId,
            touchpointId: item.id,
            id: item.latestVersion!.id,
          }),
        ),
      );
      const updatedItems = itemsToUpdate.filter((_, index) => settled[index].status === "fulfilled");
      const failedCount = itemsToUpdate.length - updatedItems.length;

      updatedItems.forEach((item) => {
        queryClient.invalidateQueries({
          queryKey: touchpointVersionKeys.listScope(item.clientId, item.campaignId, item.id),
        });
        queryClient.invalidateQueries({
          queryKey: touchpointKeys.detail(item.clientId, item.campaignId, item.id),
        });
      });
      queryClient.invalidateQueries({ queryKey: touchpointKeys.lists() });

      if (updatedItems.length === 0) {
        showUpdateFailedToast();
        return;
      }

      if (failedCount > 0) {
        toast.error({
          data: {
            title: `Failed to update merge tags for ${failedCount} of ${itemsToUpdate.length} creatives`,
          },
        });
      }

      toast.success({
        data: singleItemTypeLabel
          ? { title: `${singleItemTypeLabel} merge tags updated`, message: "New draft created" }
          : { title: "Merge tags updated", message: "New drafts created" },
      });

      onSuccess?.();
    } catch {
      showUpdateFailedToast();
    } finally {
      setIsLoading(false);
      closeUpdateMergeTagsModal();
    }
  }, [itemsToUpdate, singleItemTypeLabel, queryClient, onSuccess, closeUpdateMergeTagsModal]);

  const updateMergeTagsModalMarkup = useMemo(() => {
    const isOpen = itemsToUpdate.length > 0;
    if (!isOpen) return null;

    const itemLabel = singleItemTypeLabel?.toLowerCase() ?? "creative";
    const headline =
      itemsToUpdate.length > 1
        ? `Update merge tags for ${itemsToUpdate.length} creatives?`
        : `Update merge tags for this ${itemLabel}?`;
    const message =
      itemsToUpdate.length > 1
        ? "This creates a new draft for each selected creative with merge tags updated from its most recent version."
        : "This creates a new draft with merge tags updated from the most recent version.";

    return (
      <ConfirmationDialog
        open
        onClose={closeUpdateMergeTagsModal}
        onConfirm={handleUpdateMergeTags}
        headline={headline}
        message={message}
        confirmButtonText="Update"
        confirmLoadingMessage="Updating..."
        confirming={isLoading}
      />
    );
  }, [itemsToUpdate, singleItemTypeLabel, isLoading, closeUpdateMergeTagsModal, handleUpdateMergeTags]);

  return {
    openUpdateMergeTagsModal,
    updateMergeTagsModalMarkup,
  };
};
